import _extends from "@babel/runtime/helpers/extends";
import { RecommendationLabel } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { Box, Button, Divider, Heading, Loader, TextButton } from '@wix/design-system';
import { DomainSearchSuiteModule, SearchRecommendationLayout } from '@wix/domains-search-suite';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { ChevronDown } from '@wix/wix-ui-icons-common/lazy';
import React, { useState } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useIsHarmonyTheme } from '@wix/core-domain-search';
import { useMatchingLabels } from '../../hooks/useMatchingLabels';
import { useResultsPageConfig } from '../../hooks/useResultsPageConfig';
import { DomainSearchGateModule } from '../../module/domainSearchGate.module';
import { ExactMatchHeadline } from '../ExactMatchHeadline';
import { HeroCard, HeroCardSkeleton } from '../HeroCard';
import { HeroBusinessEmailExample } from '../HeroCard/HeroBusinessEmailExample';
import { LazyRecommendationLabel } from '../LazyRecommedationLabel/LazyRecommendationLabel';
import { AnimatedBanner } from '../AnimatedBanner';
import { GoogleWorkspaceBanner } from '../GoogleWorkspaceBanner';
import { createLeanLabelContainer, createOptimizedLabelContainer } from './suggestionLabelContainers';
import s from './Recommendations.css';
export const Recommendations = _ref => {
  var _recommendations$, _mainCardItems$;
  let {
    onInsightHoverChange,
    recommendations,
    isLoading,
    hasMore,
    fetchNextPage,
    isFetchingNextPage,
    getItButtonOverride,
    onAiInsightsClick,
    showHeroCard = false,
    isResultsPageOptimizations: isResultsPageOptimizationsProp = false,
    isLeanView = false,
    hideAlternativesHeading = false,
    hideFreeWithSitePlanBadge = false,
    pricingOptimized,
    isBusinessEmail = false,
    isPricingLoading = false,
    Layouts: layoutsOverride,
    PriceNote,
    priceNoteApplies,
    ...props
  } = _ref;
  // Business email results reuse the optimized results layout (bordered
  // suggestions box, optimized hero + labels) plus the Google Workspace content.
  const isResultsPageOptimizations = isResultsPageOptimizationsProp || isBusinessEmail;
  const leanView = isLeanView || isResultsPageOptimizations;
  const {
    data: purchasePermissions
  } = DomainsApiClient.hooks.useGetPurchasePermissions({});
  const {
    t
  } = DomainSearchGateModule.internals.useTranslations();
  const {
    resolveActionForSuggestion
  } = DomainSearchSuiteModule.SDK.useSuggestionActionResolver();
  const isMobile = useIsMobile();
  const isHarmonyTheme = useIsHarmonyTheme();
  const {
    labels: matchLabels,
    isLoading: isLoadingLabels
  } = useMatchingLabels(recommendations, isBusinessEmail && !showHeroCard ? false : leanView);
  const {
    showExactMatchHeadline: isExactMatchHeadlineVisible
  } = useResultsPageConfig();
  const [isMouseInList, setIsMouseInList] = useState(false);
  const handleGetItClick = recommendation => {
    const position = recommendations == null ? void 0 : recommendations.findIndex(_ref2 => {
      let {
        id
      } = _ref2;
      return id === recommendation.id;
    });
    DomainSearchSuiteModule.internals.bi.onGetItClick == null || DomainSearchSuiteModule.internals.bi.onGetItClick({
      domain: recommendation.domain || '',
      type: recommendation.label,
      position
    });
  };
  const onClickGetIt = async recommendation => {
    handleGetItClick(recommendation);
    if (recommendation.domain) {
      await resolveActionForSuggestion(recommendation.domain, recommendation.premium);
    }
  };
  const hasExactMatchTop = ((_recommendations$ = recommendations[0]) == null ? void 0 : _recommendations$.label) === RecommendationLabel.EXACT_MATCH;
  const heroRecommendation = showHeroCard && hasExactMatchTop ? recommendations[0] : undefined;
  const isExactMatchHero = (heroRecommendation == null ? void 0 : heroRecommendation.label) === RecommendationLabel.EXACT_MATCH;
  const businessEmailFirstRec = isBusinessEmail && !showHeroCard && !isLoading && recommendations.length > 0 ? recommendations[0] : undefined;
  const businessEmailRest = !!businessEmailFirstRec;
  const compactExactMatch = isResultsPageOptimizations && !showHeroCard && !businessEmailFirstRec && hasExactMatchTop && !isLoading ? recommendations[0] : undefined;
  const listItems = heroRecommendation || compactExactMatch || businessEmailFirstRec ? recommendations.slice(1) : recommendations;
  const showMainCards = isResultsPageOptimizations && !businessEmailFirstRec && !hasExactMatchTop && !isLoading && listItems.length >= 2;
  const mainCardItems = showMainCards ? listItems.slice(0, 2) : [];
  const mainCardLabels = ((_mainCardItems$ = mainCardItems[0]) == null ? void 0 : _mainCardItems$.label) === RecommendationLabel.GREAT_ALTERNATIVE ? [RecommendationLabel.GREAT_ALTERNATIVE, 'RECOMMENDED'] : [RecommendationLabel.BEST_MATCH, RecommendationLabel.GREAT_ALTERNATIVE];
  const listItemsToRender = showMainCards ? listItems.slice(2) : listItems;
  const showHeroListStyling = showHeroCard && (isLoading || !!heroRecommendation);
  const showAlternativesHeading = !hideAlternativesHeading && !isBusinessEmail && (!leanView || isResultsPageOptimizations) && !!heroRecommendation && !isLoading && listItems.length > 0 && !isMobile;
  const recommendationList = /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.SearchRecommendationList, {
    onHoverChange: onInsightHoverChange,
    disabled: !(purchasePermissions != null && purchasePermissions.hasPermission),
    onClickGetIt: onClickGetIt,
    isLeanView: businessEmailRest ? false : leanView,
    isResultsPageOptimizations: businessEmailRest ? false : isResultsPageOptimizations,
    pricingOptimized: pricingOptimized,
    isPricingLoading: isPricingLoading,
    Layouts: {
      ...SearchRecommendationLayout,
      ...layoutsOverride,
      LabelContainer: businessEmailRest ? LazyRecommendationLabel({
        isLoading: isLoadingLabels,
        matchLabels
      }) : isResultsPageOptimizations ? createOptimizedLabelContainer() : leanView ? createLeanLabelContainer({
        listItems,
        heroRecommendation,
        hideFreeWithSitePlanBadge,
        recommendations
      }) : LazyRecommendationLabel({
        isLoading: isLoadingLabels,
        matchLabels
      }),
      ...(getItButtonOverride && {
        GetItButton: getItButtonOverride
      }),
      ...(showHeroListStyling && !isResultsPageOptimizations && {
        HoverBox: _ref3 => {
          let {
            children,
            className: _className,
            ...rest
          } = _ref3;
          return /*#__PURE__*/React.createElement(Box, _extends({}, rest, {
            style: {
              cursor: 'pointer'
            }
          }), children);
        },
        Divider: () => /*#__PURE__*/React.createElement(Divider, null),
        GetItButton: _ref4 => {
          let {
            children,
            priority,
            ...rest
          } = _ref4;
          return /*#__PURE__*/React.createElement(Button, _extends({
            size: "medium",
            skin: "dark"
          }, rest, {
            className: s.GetItButton,
            priority: isMouseInList ? priority : 'secondary'
          }), children);
        }
      }),
      ...(businessEmailRest && {
        Divider: () => /*#__PURE__*/React.createElement(Divider, null),
        HoverBox: _ref5 => {
          let {
            children,
            className: _className,
            ...rest
          } = _ref5;
          return /*#__PURE__*/React.createElement(Box, _extends({}, rest, {
            className: s.businessEmailRow
          }), children);
        },
        GetItButton: _ref6 => {
          let {
            children,
            priority: _priority,
            className: _className,
            ...rest
          } = _ref6;
          return /*#__PURE__*/React.createElement(Button, _extends({
            size: "medium",
            skin: "dark",
            priority: "secondary"
          }, rest, {
            className: s.businessEmailGetIt
          }), children);
        }
      }),
      ...(isHarmonyTheme && {
        SearchRecommendationContainer: _ref7 => {
          let {
            className,
            ...rest
          } = _ref7;
          return /*#__PURE__*/React.createElement(SearchRecommendationLayout.SearchRecommendationContainer, _extends({}, rest, {
            backgroundColor: "white",
            className: [s.suggestionRowCard, className].filter(Boolean).join(' ')
          }));
        },
        SearchRecommendationSkeletonContainer: _ref8 => {
          let {
            className,
            ...rest
          } = _ref8;
          return /*#__PURE__*/React.createElement(SearchRecommendationLayout.SearchRecommendationSkeletonContainer, _extends({}, rest, {
            backgroundColor: "white",
            className: [s.suggestionRowCard, className].filter(Boolean).join(' ')
          }));
        }
      })
    },
    items: listItemsToRender,
    isLoading: isLoading,
    useMobileLayout: isMobile,
    onAiInsightsClick: onAiInsightsClick,
    firstSuggestionPrimary: !isResultsPageOptimizations && !heroRecommendation,
    defaultNonHovered: isResultsPageOptimizations
  });
  return /*#__PURE__*/React.createElement(Box, _extends({
    flex: 1,
    direction: "vertical",
    padding: isMobile ? 'SP1 SP4 0' : '0 48px',
    gap: showHeroListStyling && !isResultsPageOptimizations ? 'SP6' : undefined
  }, props), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical"
  }, isExactMatchHero && (heroRecommendation == null ? void 0 : heroRecommendation.domain) && !isMobile && isExactMatchHeadlineVisible && /*#__PURE__*/React.createElement(ExactMatchHeadline, {
    domain: heroRecommendation.domain
  }), (showHeroCard || isResultsPageOptimizations) && isLoading && !heroRecommendation && /*#__PURE__*/React.createElement(HeroCardSkeleton, {
    isResultsPageOptimizations: isResultsPageOptimizations
  }), heroRecommendation && /*#__PURE__*/React.createElement(HeroCard, {
    recommendation: heroRecommendation,
    onClickGetIt: onClickGetIt,
    onAiInsightsClick: onAiInsightsClick,
    disabled: !(purchasePermissions != null && purchasePermissions.hasPermission),
    isResultsPageOptimizations: isResultsPageOptimizations,
    inlineMobileActions: leanView,
    businessEmailExample: isBusinessEmail,
    Label: layoutsOverride == null ? void 0 : layoutsOverride.Label,
    PriceNote: PriceNote,
    priceNoteApplies: priceNoteApplies,
    isPricingLoading: isPricingLoading
  }), compactExactMatch && /*#__PURE__*/React.createElement(HeroCard, {
    recommendation: compactExactMatch,
    onClickGetIt: onClickGetIt,
    disabled: !(purchasePermissions != null && purchasePermissions.hasPermission),
    isResultsPageOptimizations: true,
    compact: true,
    businessEmailExample: isBusinessEmail,
    Label: layoutsOverride == null ? void 0 : layoutsOverride.Label,
    PriceNote: PriceNote,
    priceNoteApplies: priceNoteApplies,
    dataHook: "exact-match-card",
    isPricingLoading: isPricingLoading
  }), showMainCards && /*#__PURE__*/React.createElement(Box, {
    direction: isMobile ? 'vertical' : 'horizontal',
    gap: "16px",
    marginTop: isMobile ? undefined : '16px'
  }, mainCardItems.map((rec, index) => /*#__PURE__*/React.createElement(HeroCard, {
    key: rec.id ?? rec.domain,
    recommendation: rec,
    onClickGetIt: onClickGetIt,
    disabled: !(purchasePermissions != null && purchasePermissions.hasPermission),
    isResultsPageOptimizations: true,
    compact: true,
    badgeLabel: mainCardLabels[index],
    ctaPriority: index === 0 ? 'primary' : 'secondary',
    businessEmailExample: isBusinessEmail,
    Label: layoutsOverride == null ? void 0 : layoutsOverride.Label,
    PriceNote: PriceNote,
    priceNoteApplies: priceNoteApplies,
    dataHook: `main-card-${index}`,
    isPricingLoading: isPricingLoading
  }))), businessEmailFirstRec && /*#__PURE__*/React.createElement(Box, {
    borderRadius: isHarmonyTheme ? undefined : '12px',
    border: isHarmonyTheme ? undefined : '1px solid var(--wds-color-border-disabled, #e5e5e5)',
    className: isHarmonyTheme ? undefined : s.suggestionsContainer
  }, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.SearchRecommendationList, {
    disabled: !(purchasePermissions != null && purchasePermissions.hasPermission),
    onClickGetIt: onClickGetIt,
    items: [businessEmailFirstRec],
    useMobileLayout: isMobile,
    firstSuggestionPrimary: true,
    defaultNonHovered: true,
    isPricingLoading: isPricingLoading,
    Layouts: {
      ...SearchRecommendationLayout,
      ...layoutsOverride,
      ...(getItButtonOverride && {
        GetItButton: getItButtonOverride
      }),
      HoverBox: _ref9 => {
        let {
          children,
          className: _className,
          ...rest
        } = _ref9;
        return /*#__PURE__*/React.createElement(Box, rest, children);
      },
      TitleDisplay: titleProps => /*#__PURE__*/React.createElement(Box, {
        direction: "vertical",
        gap: "2px"
      }, /*#__PURE__*/React.createElement(SearchRecommendationLayout.TitleDisplay, titleProps), /*#__PURE__*/React.createElement(Box, {
        paddingLeft: businessEmailFirstRec.label === RecommendationLabel.EXACT_MATCH ? 'SP4' : undefined
      }, /*#__PURE__*/React.createElement(HeroBusinessEmailExample, {
        domain: businessEmailFirstRec.domain ?? '',
        size: "small"
      })))
    }
  })), (!!heroRecommendation || !!compactExactMatch || showMainCards || !!businessEmailFirstRec) && (isBusinessEmail ? /*#__PURE__*/React.createElement(GoogleWorkspaceBanner, null) : isResultsPageOptimizations && /*#__PURE__*/React.createElement(AnimatedBanner, null))), /*#__PURE__*/React.createElement("div", {
    onMouseEnter: showHeroListStyling ? () => setIsMouseInList(true) : undefined,
    onMouseLeave: showHeroListStyling ? () => setIsMouseInList(false) : undefined
  }, showAlternativesHeading && /*#__PURE__*/React.createElement(Box, {
    marginTop: "18px",
    marginBottom: isResultsPageOptimizations ? '32px' : undefined
  }, /*#__PURE__*/React.createElement(Heading, {
    size: "medium",
    as: "h2"
  }, t('domain_search_gate.results.alternative_suggestion_list.title'))), isResultsPageOptimizations ? /*#__PURE__*/React.createElement(Box, {
    borderRadius: isHarmonyTheme ? undefined : '12px',
    border: isHarmonyTheme ? undefined : '1px solid var(--wds-color-border-disabled, #e5e5e5)',
    className: isHarmonyTheme ? undefined : s.suggestionsContainer
  }, recommendationList) : recommendationList), hasMore && /*#__PURE__*/React.createElement(Box, {
    flex: 1,
    align: "center",
    padding: "SP8 SP4 SP10 SP4"
  }, /*#__PURE__*/React.createElement(TextButton, {
    skin: "dark",
    onClick: () => fetchNextPage(),
    suffixIcon: isFetchingNextPage ? /*#__PURE__*/React.createElement(Box, {
      marginLeft: "3px",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Loader, {
      size: "tiny"
    })) : /*#__PURE__*/React.createElement(ChevronDown, null)
  }, t('domain_search_gate.results.show_more'))));
};
//# sourceMappingURL=Recommendations.js.map